import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, CheckCircle, XCircle, Sparkle, Warning } from "@phosphor-icons/react";
import { BloomButton } from "@/components/styleguide/ui/BloomButton";
import { BloomInput } from "@/components/styleguide/ui/BloomInput";
import { cn } from "@/lib/utils";
import { api } from "../api";

const WORD_TYPES   = ["noun", "verb", "adjective", "adverb", "other"];
const DIFFICULTIES = ["easy", "medium", "hard"];
const toList  = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);
const toLines = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);

/* ── Usage pill ──────────────────────────────────────────────────────────── */
function UsagePill({ usage }) {
  if (!usage) return null;
  const pct   = usage.count / usage.limit;
  const state = usage.at_limit ? "full" : pct >= 0.8 ? "warn" : "ok";
  const cls   = {
    ok:   "bg-muted border-border text-muted-foreground",
    warn: "bg-warning/15 border-warning/30 text-warning-ink",
    full: "bg-destructive/10 border-destructive/30 text-destructive",
  }[state];
  const dot = {
    ok:   "bg-muted-foreground",
    warn: "bg-warning-ink",
    full: "bg-destructive",
  }[state];
  return (
    <span className={cn("inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-bold", cls)}>
      <span className={cn("h-1.5 w-1.5 rounded-full shrink-0", dot)} />
      {usage.count} / {usage.limit} today
    </span>
  );
}

/* ── Main component ──────────────────────────────────────────────────────── */
export function WordEditor({ word, presetWord, similarTo, onClose, onSaved }) {
  const isNew     = !word?.word_id;
  const isSimilar = !word && !!similarTo;

  const [f,          setF]          = useState({});
  const [saving,     setSaving]     = useState(false);
  const [generating, setGenerating] = useState(false);
  const [uploading,  setUploading]  = useState(false);
  const [error,      setError]      = useState("");
  const [usage,      setUsage]      = useState(null);
  const [aiMeta,     setAiMeta]     = useState(null); // { corrected, similarWords }

  const imageInputRef = useRef(null);

  /* ── Initialise form ─────────────────────────────────────────────────── */
  useEffect(() => {
    if (isSimilar) {
      setF({
        word:                  "",
        word_type:             similarTo.word_type             || "",
        meaning:               "",
        synonyms:              (similarTo.synonyms  || []).join(", "),
        antonyms:              (similarTo.antonyms  || []).join(", "),
        sentences:             "",
        memory_hint:           similarTo.memory_hint           || "",
        difficulty:            similarTo.difficulty            || "",
        years:                 similarTo.years                 || [],
        gs_11plus:             similarTo.gs_11plus             || false,
        tags:                  (similarTo.tags     || []).join(", "),
        image_url:             similarTo.image_url             ?? "",
        image_label:           "",
        image_caption:         similarTo.image_caption         ?? "",
        antonym_image_url:     similarTo.antonym_image_url     ?? "",
        antonym_image_label:   "",
        antonym_image_caption: similarTo.antonym_image_caption ?? "",
      });
      setError(""); setAiMeta(null);
      return;
    }
    setF({
      word:                  word?.word                || presetWord || "",
      word_type:             word?.word_type           || "",
      meaning:               word?.meaning             || "",
      synonyms:              (word?.synonyms  || []).join(", "),
      antonyms:              (word?.antonyms  || []).join(", "),
      sentences:             (word?.sentences || []).join("\n"),
      memory_hint:           word?.memory_hint         || "",
      difficulty:            word?.difficulty          || "",
      years:                 word?.years               || [],
      gs_11plus:             word?.gs_11plus           || false,
      tags:                  (word?.tags      || []).join(", "),
      image_url:             word?.image_url            ?? "",
      image_label:           word?.image_label          ?? (word?.word ? word.word.toUpperCase() : ""),
      image_caption:         word?.image_caption        ?? "",
      antonym_image_url:     word?.antonym_image_url    ?? "",
      antonym_image_label:   word?.antonym_image_label  ?? (word?.antonyms?.[0] ? word.antonyms[0].toUpperCase() : ""),
      antonym_image_caption: word?.antonym_image_caption ?? "",
    });
    setError(""); setAiMeta(null);
  }, [word, presetWord, similarTo, isSimilar]);

  /* ── Fetch usage on open (new words only) ────────────────────────────── */
  useEffect(() => {
    if (isNew && !isSimilar) {
      api("/admin/ai/usage").then(setUsage).catch(() => {});
    }
  }, [isNew, isSimilar]);

  const set = (k, v) => {
    setF((p) => ({ ...p, [k]: v }));
    // Clear the "already exists" banner once admin edits anything (re-enables save),
    // but KEEP existingWordId so the save routes to PATCH not POST.
    if (aiMeta?.existingWord) {
      setAiMeta((prev) => ({ ...prev, existingWord: null }));
    }
  };

  /* ── Fill with Bloom ─────────────────────────────────────────────────── */
  const fillWithBloom = async () => {
    const wordVal = f.word?.trim();
    if (!wordVal || generating) return;
    setGenerating(true);
    setError("");
    setAiMeta(null);
    try {
      const hasImage = !!f.image_url?.trim();
      const result = await api("/admin/ai/generate", {
        method: "POST",
        body:   { word: wordVal, skip_image: hasImage },
      });

      if (result.already_exists) {
        // Word found in library — pre-fill the form so admin can see it,
        // but flag it so save buttons are disabled.
        const ew = result.existing_word;
        setF((prev) => ({
          ...prev,
          word:        ew.word,
          word_type:   ew.word_type  || prev.word_type,
          meaning:     ew.meaning    || "",
          synonyms:    (ew.synonyms  || []).join(", "),
          antonyms:    (ew.antonyms  || []).join(", "),
          sentences:   (ew.sentences || []).join("\n"),
          difficulty:  ew.difficulty || "",
          years:       ew.years      || [],
          gs_11plus:   ew.gs_11plus  ?? prev.gs_11plus,
          image_url:   ew.image_url  || prev.image_url,
          image_label: ew.image_label || ew.word?.toUpperCase() || prev.image_label,
        }));
        setAiMeta({
          corrected:      result.corrected || false,
          similarWords:   [],
          existingWord:   ew.word,      // shows blue banner
          existingWordId: ew.word_id,   // routes save to PATCH instead of POST
        });
        if (result.usage) setUsage(result.usage);
        return;
      }

      setF((prev) => ({
        ...prev,
        word:        result.word,
        word_type:   result.word_type  || prev.word_type,
        meaning:     result.meaning    || "",
        synonyms:    (result.synonyms  || []).join(", "),
        antonyms:    (result.antonyms  || []).join(", "),
        sentences:   (result.sentences || []).join("\n"),
        difficulty:  result.difficulty || "",
        years:       result.years      || [],
        gs_11plus:   result.gs_11plus  ?? prev.gs_11plus,
        image_url:   result.image_url  || prev.image_url,
        image_label: result.image_label || result.word?.toUpperCase() || prev.image_label,
      }));

      setAiMeta({
        corrected:    result.corrected,
        similarWords: result.similar_words || [],
        existingWord: null,
      });
      if (result.usage) setUsage(result.usage);

    } catch (e) {
      if (e.status === 429) {
        setUsage((prev) => prev ? { ...prev, at_limit: true, count: prev.limit } : null);
      }
      setError(e.message || "Generation failed — please try again.");
    } finally {
      setGenerating(false);
    }
  };

  /* ── Regenerate image only ───────────────────────────────────────────── */
  const handleRegenerateImage = async () => {
    const wordVal = f.word?.trim();
    if (!wordVal || generating) return;
    if (!window.confirm(`Regenerate image for "${wordVal}"? This uses 1 generation credit.`)) return;
    setGenerating(true);
    setError("");
    try {
      const result = await api("/admin/ai/regenerate-image", {
        method: "POST",
        body: {
          word:      wordVal,
          meaning:   f.meaning || null,
          sentences: toLines(f.sentences || ""),
        },
      });
      set("image_url",   result.image_url);
      set("image_label", result.image_label);
      if (result.usage) setUsage(result.usage);
    } catch (e) {
      if (e.status === 429) {
        setUsage((prev) => prev ? { ...prev, at_limit: true } : null);
      }
      setError(e.message || "Image regeneration failed.");
    } finally {
      setGenerating(false);
    }
  };

  /* ── Manual image file upload ────────────────────────────────────────── */
  const handleImageFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const formData = new FormData();
      formData.append("file", file);
      const wordVal = f.word?.trim() || "word";
      const resp = await fetch(
        `/api/admin/ai/upload-image?word=${encodeURIComponent(wordVal)}`,
        { method: "POST", body: formData, credentials: "include" }
      );
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || "Upload failed");
      }
      const result = await resp.json();
      set("image_url",   result.image_url);
      set("image_label", result.image_label);
    } catch (e) {
      setError(e.message || "Image upload failed.");
    } finally {
      setUploading(false);
      if (imageInputRef.current) imageInputRef.current.value = "";
    }
  };

  /* ── Save ────────────────────────────────────────────────────────────── */
  const save = async (status) => {
    if (!f.word?.trim()) { setError("Word is required"); return; }
    setSaving(true);
    setError("");
    const body = {
      word:                  f.word.trim(),
      word_type:             f.word_type  || null,
      meaning:               f.meaning    || null,
      synonyms:              toList(f.synonyms),
      antonyms:              toList(f.antonyms),
      sentences:             toLines(f.sentences),
      memory_hint:           f.memory_hint || null,   // preserved, not shown in UI
      difficulty:            f.difficulty  || null,
      years:                 f.years,
      gs_11plus:             f.gs_11plus,
      tags:                  toList(f.tags),
      image_url:             f.image_url?.trim()             ?? "",
      image_label:           f.image_label?.trim()           ?? "",
      image_caption:         f.image_caption?.trim()         ?? "",
      antonym_image_url:     f.antonym_image_url?.trim()     ?? "",
      antonym_image_label:   f.antonym_image_label?.trim()   ?? "",
      antonym_image_caption: f.antonym_image_caption?.trim() ?? "",
    };
    if (status) body.status = status;
    try {
      // existingWordId is set when "Fill with Bloom" loaded an existing word.
      // In that case always PATCH (update), even though the modal was opened as "New word".
      const targetId = aiMeta?.existingWordId || word?.word_id;
      if (targetId) {
        await api(`/admin/words/${targetId}`, { method: "PATCH", body });
      } else {
        await api("/admin/words", { method: "POST", body });
      }
      onSaved();
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const atLimit          = usage?.at_limit;
  const hasUploadedImage = !!f.image_url?.trim();
  const canFill          = isNew && !isSimilar && !!f.word?.trim() && !generating && !atLimit;

  /* ── Render ──────────────────────────────────────────────────────────── */
  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-end justify-center sm:items-center"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      >
        <button aria-label="Close" onClick={onClose}
          className="absolute inset-0 bg-foreground/25 backdrop-blur-[2px]" />

        <motion.div
          data-testid="word-editor"
          initial={{ y: 60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ type: "spring", stiffness: 300, damping: 26 }}
          className="relative flex max-h-[92svh] w-full max-w-2xl flex-col rounded-t-[2rem] bg-card shadow-soft-lg sm:rounded-[2rem]"
        >
          {/* ── Header ── */}
          <div className="flex items-center justify-between px-6 pt-5">
            <div>
              <h2 className="font-display text-xl font-black text-foreground">
                {isSimilar ? `Similar word to "${similarTo.word}"` : isNew ? "New word" : `Edit "${word.word}"`}
              </h2>
              {isSimilar && (
                <p className="text-xs font-semibold text-primary-strong">
                  Fill in word, meaning &amp; sentences — everything else is copied ✓
                </p>
              )}
              {!isNew && !isSimilar && (
                <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
                  Status: {word.status} · Source: {word.source}
                </p>
              )}
            </div>
            <button onClick={onClose} data-testid="editor-close" aria-label="Close"
              className="flex h-11 w-11 items-center justify-center rounded-full bg-muted transition hover:bg-border">
              <X size={18} weight="bold" />
            </button>
          </div>

          {/* ── Scrollable body ── */}
          <div className="scroll-soft min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-4">

            {/* Word + type */}
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <BloomInput
                  label={isSimilar ? "Word ✱ required" : "Word"}
                  value={f.word || ""}
                  data-testid="editor-word"
                  onChange={(e) => set("word", e.target.value)}
                  className={isSimilar ? "ring-2 ring-primary/40 rounded-2xl" : ""}
                />
                {/* Fill with Bloom — new words only */}
                {isNew && !isSimilar && (
                  <div className="flex flex-col gap-1.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        disabled={!canFill}
                        onClick={fillWithBloom}
                        className={cn(
                          "flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-extrabold transition",
                          canFill
                            ? "border-primary/40 bg-accent text-primary-strong hover:opacity-80"
                            : "cursor-not-allowed border-border bg-muted text-muted-foreground opacity-60"
                        )}
                      >
                        <Sparkle
                          size={13}
                          weight="fill"
                          className={generating ? "animate-pulse" : ""}
                        />
                        {generating
                          ? (hasUploadedImage ? "Generating details…" : "Filling…")
                          : atLimit ? "Limit reached"
                          : "Fill with Bloom"}
                      </button>
                      <UsagePill usage={usage} />
                    </div>
                    {hasUploadedImage && !generating && (
                      <p className="text-[11px] font-semibold text-muted-foreground">
                        Image uploaded — will generate details only, no image credit used
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div>
                <p className="mb-2 text-sm font-bold text-foreground">Word type</p>
                <div className="flex flex-wrap gap-1.5">
                  {WORD_TYPES.map((t) => (
                    <button key={t} data-testid={`editor-type-${t}`}
                      onClick={() => set("word_type", f.word_type === t ? "" : t)}
                      className={cn(
                        "rounded-full border-2 px-3 py-1.5 text-xs font-extrabold transition",
                        f.word_type === t
                          ? "border-primary bg-accent text-primary-strong"
                          : "border-border text-muted-foreground"
                      )}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Error — shown at top so it's immediately visible */}
            {error && (
              <div className="flex items-start gap-2 rounded-2xl border border-destructive/25 bg-destructive/10 px-4 py-2.5 text-sm font-semibold text-destructive" data-testid="editor-error">
                {error}
              </div>
            )}

            {/* AI feedback banners */}
            {aiMeta?.existingWord && (
              <div className="flex items-start gap-2 rounded-2xl border border-blue/25 bg-blue/8 px-4 py-2.5 text-sm font-semibold text-blue-ink">
                <CheckCircle size={15} weight="fill" className="mt-0.5 shrink-0" />
                <span>
                  <strong>"{aiMeta.existingWord}"</strong> is already in your library — existing content loaded.
                  Edit any field and save to update it.
                </span>
              </div>
            )}
            {aiMeta?.corrected && !aiMeta?.existingWord && (
              <div className="flex items-center gap-2 rounded-2xl border border-success/25 bg-success/10 px-4 py-2.5 text-sm font-semibold text-success-ink">
                <CheckCircle size={15} weight="fill" />
                Spell-checked to <strong>"{f.word}"</strong>
              </div>
            )}
            {aiMeta?.similarWords?.length > 0 && (
              <div className="flex items-start gap-2 rounded-2xl border border-warning/25 bg-warning/10 px-4 py-2.5 text-sm font-semibold text-warning-ink">
                <Warning size={15} weight="fill" className="mt-0.5 shrink-0" />
                <span>
                  Similar words already in library:{" "}
                  <strong>{aiMeta.similarWords.map((w) => w.word).join(", ")}</strong>
                </span>
              </div>
            )}
            {atLimit && (
              <div className="flex items-center gap-2 rounded-2xl border border-destructive/25 bg-destructive/10 px-4 py-2.5 text-sm font-semibold text-destructive">
                <Warning size={15} weight="fill" />
                Daily limit reached ({usage.limit} generations). Resets {usage.resets_at} — or upload an image manually below.
              </div>
            )}

            {/* Meaning */}
            <div>
              <p className="mb-2 text-sm font-bold text-foreground">
                Child-friendly meaning{isSimilar ? " ✱ required" : ""}
              </p>
              <textarea
                value={f.meaning || ""}
                data-testid="editor-meaning"
                onChange={(e) => set("meaning", e.target.value)}
                rows={2}
                className={cn(
                  "w-full rounded-2xl border-2 bg-background px-4 py-3 text-sm font-semibold text-foreground focus:border-primary focus:outline-none",
                  isSimilar ? "border-primary/50 ring-2 ring-primary/30" : "border-border"
                )}
              />
            </div>

            {/* Synonyms + Antonyms */}
            <div className="grid gap-4 sm:grid-cols-2">
              <BloomInput label="Synonyms (comma separated)" value={f.synonyms || ""}
                data-testid="editor-synonyms" onChange={(e) => set("synonyms", e.target.value)} />
              <BloomInput label="Antonyms (comma separated)" value={f.antonyms || ""}
                data-testid="editor-antonyms" onChange={(e) => set("antonyms", e.target.value)} />
            </div>

            {/* Sentences */}
            <div>
              <p className="mb-2 text-sm font-bold text-foreground">
                Example sentences (one per line){isSimilar ? " ✱ required" : ""}
              </p>
              <textarea
                value={f.sentences || ""}
                data-testid="editor-sentences"
                onChange={(e) => set("sentences", e.target.value)}
                rows={3}
                className={cn(
                  "w-full rounded-2xl border-2 bg-background px-4 py-3 text-sm font-semibold text-foreground focus:border-primary focus:outline-none",
                  isSimilar ? "border-primary/50 ring-2 ring-primary/30" : "border-border"
                )}
              />
            </div>

            {/* Difficulty + Years + 11+ */}
            <div className="flex flex-wrap items-end gap-5">
              <div>
                <p className="mb-2 text-sm font-bold text-foreground">Difficulty</p>
                <div className="flex gap-1.5">
                  {DIFFICULTIES.map((d) => (
                    <button key={d} data-testid={`editor-diff-${d}`}
                      onClick={() => set("difficulty", f.difficulty === d ? "" : d)}
                      className={cn(
                        "rounded-full border-2 px-3 py-1.5 text-xs font-extrabold transition",
                        f.difficulty === d ? "border-primary bg-accent text-primary-strong" : "border-border text-muted-foreground"
                      )}>
                      {d}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="mb-2 text-sm font-bold text-foreground">Years</p>
                <div className="flex gap-1.5">
                  {[3, 4, 5, 6].map((y) => (
                    <button key={y} data-testid={`editor-year-${y}`}
                      onClick={() => set("years", f.years.includes(y)
                        ? f.years.filter((x) => x !== y)
                        : [...f.years, y].sort()
                      )}
                      className={cn(
                        "rounded-full border-2 px-3 py-1.5 text-xs font-extrabold transition",
                        f.years?.includes(y) ? "border-primary bg-accent text-primary-strong" : "border-border text-muted-foreground"
                      )}>
                      Y{y}
                    </button>
                  ))}
                </div>
              </div>

              <label className="flex cursor-pointer items-center gap-2 pb-1.5 text-sm font-bold text-foreground">
                <input type="checkbox" checked={f.gs_11plus || false} data-testid="editor-gs"
                  onChange={(e) => set("gs_11plus", e.target.checked)}
                  className="h-5 w-5 accent-[#FF7A45]" />
                Grammar School / 11+
              </label>
            </div>

            <BloomInput label="Tags (comma separated)" value={f.tags || ""}
              data-testid="editor-tags" onChange={(e) => set("tags", e.target.value)} />

            {/* ── Images ─────────────────────────────────────────────────── */}
            <div className="border-t border-border pt-4">
              <p className="mb-3 text-sm font-bold text-foreground">Images</p>
              <div className="grid gap-5 sm:grid-cols-2">

                {/* Word image */}
                <div className="space-y-2.5">
                  <p className="text-xs font-extrabold uppercase tracking-wide text-teal-ink">Word image</p>

                  {/* Preview */}
                  {f.image_url ? (
                    <div className="relative overflow-hidden rounded-xl border border-border">
                      <img src={f.image_url} alt="Word image preview"
                        className="h-28 w-full object-cover"
                        onError={(e) => { e.currentTarget.style.display = "none"; }} />
                      <button type="button"
                        onClick={() => { set("image_url", ""); set("image_label", ""); }}
                        className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-foreground/60 text-white hover:bg-foreground/80"
                        title="Remove image">
                        <X size={12} weight="bold" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex h-20 items-center justify-center rounded-xl border-2 border-dashed border-border bg-muted/30 text-xs font-semibold text-muted-foreground">
                      No image yet
                    </div>
                  )}

                  {/* Action buttons */}
                  <div className="flex flex-wrap gap-2">
                    <input ref={imageInputRef} type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden" onChange={handleImageFileChange} />
                    <button type="button"
                      disabled={uploading || generating}
                      onClick={() => imageInputRef.current?.click()}
                      className="flex items-center gap-1.5 rounded-full border border-border bg-muted/50 px-3 py-1.5 text-xs font-bold text-muted-foreground transition hover:bg-muted disabled:opacity-50">
                      {uploading ? "Uploading…" : "Upload image"}
                    </button>
                    {f.image_url && (
                      <button type="button"
                        disabled={uploading || generating || atLimit}
                        onClick={handleRegenerateImage}
                        title={atLimit ? "Daily limit reached" : "Generate a new image (uses 1 credit)"}
                        className="flex items-center gap-1.5 rounded-full border border-border bg-muted/50 px-3 py-1.5 text-xs font-bold text-muted-foreground transition hover:bg-muted disabled:opacity-50">
                        <Sparkle size={11} weight="fill" />
                        {generating ? "Generating…" : "Regenerate"}
                      </button>
                    )}
                  </div>

                  {/* URL + labels */}
                  <BloomInput label="Or paste Cloudinary URL"
                    value={f.image_url || ""}
                    data-testid="editor-image-url"
                    onChange={(e) => set("image_url", e.target.value)}
                    placeholder="https://res.cloudinary.com/…" />
                  <BloomInput label="Label (shown above image)"
                    value={f.image_label || ""}
                    data-testid="editor-image-label"
                    onChange={(e) => set("image_label", e.target.value)}
                    placeholder={f.word ? f.word.toUpperCase() : "e.g. LINGER"} />
                  <BloomInput label="Caption (shown below image)"
                    value={f.image_caption || ""}
                    data-testid="editor-image-caption"
                    onChange={(e) => set("image_caption", e.target.value)}
                    placeholder="e.g. Just a few more minutes..." />
                </div>

                {/* Antonym image */}
                <div className="space-y-2.5">
                  <p className="text-xs font-extrabold uppercase tracking-wide text-destructive-ink">Antonym image (opposite)</p>

                  {f.antonym_image_url ? (
                    <div className="relative overflow-hidden rounded-xl border border-border">
                      <img src={f.antonym_image_url} alt="Antonym image preview"
                        className="h-28 w-full object-cover"
                        onError={(e) => { e.currentTarget.style.display = "none"; }} />
                      <button type="button"
                        onClick={() => { set("antonym_image_url", ""); set("antonym_image_label", ""); }}
                        className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-foreground/60 text-white hover:bg-foreground/80"
                        title="Remove image">
                        <X size={12} weight="bold" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex h-20 items-center justify-center rounded-xl border-2 border-dashed border-border bg-muted/30 text-xs font-semibold text-muted-foreground">
                      No image yet
                    </div>
                  )}

                  <BloomInput label="Or paste Cloudinary URL"
                    value={f.antonym_image_url || ""}
                    data-testid="editor-antonym-image-url"
                    onChange={(e) => set("antonym_image_url", e.target.value)}
                    placeholder="https://res.cloudinary.com/…" />
                  <BloomInput label="Label (shown above image)"
                    value={f.antonym_image_label || ""}
                    data-testid="editor-antonym-image-label"
                    onChange={(e) => set("antonym_image_label", e.target.value)}
                    placeholder={f.antonyms ? f.antonyms.split(",")[0]?.trim().toUpperCase() : "e.g. HURRY"} />
                  <BloomInput label="Caption (shown below image)"
                    value={f.antonym_image_caption || ""}
                    data-testid="editor-antonym-image-caption"
                    onChange={(e) => set("antonym_image_caption", e.target.value)}
                    placeholder="e.g. I'm late!" />
                </div>
              </div>
            </div>


          </div>

          {/* ── Footer ── */}
          <div className="flex flex-wrap items-center gap-2 border-t border-border px-6 py-4">
            <BloomButton size="sm" onClick={() => save(null)}
              disabled={saving} data-testid="editor-save">
              {saving ? "Saving…" : "Save"}
            </BloomButton>
            {(isNew || word?.status !== "approved") && (
              <BloomButton size="sm" variant="teal" leftIcon={CheckCircle}
                onClick={() => save("approved")} disabled={saving}
                data-testid="editor-approve">
                Save & Approve
              </BloomButton>
            )}
            {!isNew && word?.status !== "rejected" && (
              <BloomButton size="sm" variant="ghost" leftIcon={XCircle}
                onClick={() => save("rejected")} disabled={saving}
                className="text-destructive" data-testid="editor-reject">
                Reject
              </BloomButton>
            )}
            {aiMeta?.existingWordId && !isNew === false && (
              <span className="ml-auto text-xs font-semibold text-muted-foreground">
                Saving will update the existing word
              </span>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
